import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useLocation, match, Route, Redirect } from "react-router-dom";
import "../../styles/css/blueprint_editor.css";
import {
  BlueprintStepTemplate,
  Blueprint,
  BlueprintStep,
  AddStepRelation,
  BlueprintRunnerExecutionResponse,
  BlueprintSwitchStep,
  BlueprintStepTemplateBase,
  BlueprintStepType,
  BlueprintTrigger,
  BlueprintStepOrGhostStepOrTriggerOrScraper,
  BlueprintWithTrigger,
  BlueprintTriggerIntervalUnit,
  BlueprintTriggerSpeed,
  BlueprintTriggerSchedule,
  JSONObjectSchema,
  StepRelations,
  BlueprintGenericStepTemplate,
  BlueprintVersion,
  ReportFile,
  BlueprintParameterValue,
  BlueprintVersionStaleParametersInfo,
  BlueprintIfElseStep,
  BlueprintSelectedStepLog,
  StepLoggingViewEnum,
  StepParameterTracingTarget,
} from "../../models/Blueprints";
import BlueprintContextProvider from "./context/BlueprintContextProvider";
import BlueprintEditorView from "./BlueprintEditorView";
import {
  addNewCopiedStep,
  addNewStepFromTemplate,
  deleteStep,
  deleteSteps,
  collapseSubsteps,
  modifySwitchOption,
  ModifySwitchOptionType,
  renameBlueprintStep,
  setHumanNameForBlueprint,
  setParameterSchemaForBlueprint,
  setReturnSchemaForBlueprint,
  updateBlueprintForNewMockResponseBody,
  updateBlueprintForNewParameterValue,
  updateBlueprintForNewParameterValues,
  updateBlueprintForNewStepTemplate,
  updateBlueprintStepConcurrency,
  updateUsePaginationTimestamp,
  updateClosesPaginationSequence,
  setStepNoteForBlueprint,
  initializeStepRelationMap,
  addNewGenericStepFromTemplate,
  getBlueprintStepForStepID,
  deleteIfElseStepAndMoveChildrenUp,
  setQBXMLQueryRequestFormatForBlueprint,
  setCategoryForBlueprint,
  updateIgnoreAuthConfig,
  updateNeedsRemoteData,
  updateAllowIncompatibleTypes,
  updateFreeFileAfterUpload,
  getAllStepsForBlueprint,
  getStaleParameterValueKeysForStep,
  filterParameterValuesForNewTemplate,
  updateStep,
} from "./utils/BlueprintEditorUtils";
import {
  fetchBlueprintVersionForIntegration,
  fetchBlueprintVersions,
  fetchGenericStepTemplates,
  fetchReportFilesByBlueprintVersionID,
  fetchStaleParametersByBlueprintVersionID,
  fetchStepTemplates,
  fetchCanaryTestInfo,
  fetchCodeBlockTemplate,
} from "./utils/BlueprintEditorAPIClient";
import { fetchTemplateConfigs } from "../integrations/utils/IntegrationsAPIClient";

import EditorLeavingGuard from "../shared/unsaved-changes/EditorLeavingGuard";
import useBlueprintReducer from "./useBlueprintReducer";
import isEqual from "lodash/isEqual";
import { GlobalHotKeys } from "react-hotkeys";
import { BLUEPRINT_EDITOR_PATH, getBlueprintEditorPath } from "../../router/RouterUtils";
import EmptyStateWrapper from "../shared/layout/EmptyStateWrapper";
import { showErrorToast } from "../shared/Toasts";
import { IntegrationCommonModelConfig } from "../../models/Entities";
import {
  BLUEPRINT_OPERATION_TYPES_WITH_SCHEDULES,
  CodeBlockBranch,
  CodeBlockBranchValue,
} from "../../constants";
import useDocumentTitle from "../shared/useDocumentTitle";
import { computeTracedStepIDsForBlueprint } from "./utils/blueprint-step-tracing-utils/BlueprintStepTracingUtils";
import { CanaryTestInfo } from "../integrations/publish-requests/types";

type RouteParams = {
  integrationID: string;
  blueprintVersionID: string;
};

type Props = {
  match: match;
};

function BlueprintEditor({ match }: Props): JSX.Element {
  const { integrationID, blueprintVersionID } = useParams<RouteParams>();
  const { search } = useLocation();
  const [originalBlueprint, setOriginalBlueprint] = useState<Blueprint | undefined>();
  const [blueprintTrigger, setBlueprintTrigger] = useState<BlueprintTrigger | undefined>();
  const [
    blueprintRunnerExecutionResponse,
    setBlueprintRunnerExecutionResponse,
  ] = useState<null | BlueprintRunnerExecutionResponse>(null);
  const [selectedStep, setSelectedStep] = useState<BlueprintStepOrGhostStepOrTriggerOrScraper>();
  const [selectedSteps, setSelectedSteps] = useState<
    Array<BlueprintStepOrGhostStepOrTriggerOrScraper>
  >();
  const [copiedSteps, setCopiedSteps] = useState<
    Array<BlueprintStepOrGhostStepOrTriggerOrScraper>
  >();
  const [stepTemplates, setStepTemplates] = useState<BlueprintStepTemplate[]>([]);
  const [genericStepTemplates, setGenericStepTemplates] = useState<BlueprintGenericStepTemplate[]>(
    []
  );
  const [reportFiles, setReportFiles] = useState<Array<ReportFile>>([]);
  const [
    staleParameters,
    setStaleParameters,
  ] = useState<BlueprintVersionStaleParametersInfo | null>(null);
  const [frontendStaleParameters, setFrontendStaleParameters] = useState<Map<string, Set<string>>>(
    new Map()
  );
  const [copiedStep, setCopiedStep] = useState<BlueprintStep>();
  const [doesBlueprintHaveUnsavedChanges, setDoesBlueprintHaveUnsavedChanges] = useState<boolean>(
    false
  );
  const [blueprintState, blueprintActions] = useBlueprintReducer(undefined);
  const [availableTemplateConfigs, setAvailableTemplateConfigs] = useState<
    IntegrationCommonModelConfig[]
  >([]);
  const [stepRelationMap, setStepRelationMap] = useState<Map<string, StepRelations>>(new Map());
  const [blueprintVersions, setBlueprintVersions] = useState<BlueprintVersion[]>([]);
  const [selectedStepLog, setSelectedStepLog] = useState<BlueprintSelectedStepLog | undefined>();
  const [stepLoggingView, setStepLoggingView] = useState<StepLoggingViewEnum>(
    StepLoggingViewEnum.DEFAULT
  );
  const [isTracing, setIsTracing] = useState<boolean>(false);
  const [stepParameterToTrace, setStepParameterToTrace] = useState<
    StepParameterTracingTarget | undefined
  >();
  const [tracedStepIDs, setTracedStepIDs] = useState<string[]>([]);
  const [scrollToStep, setScrollToStep] = useState<(stepID: string) => void>(() => () => {});
  const registerScrollToStep = useCallback(
    (scrollToStepFn: (stepID: string) => void) => setScrollToStep(() => scrollToStepFn),
    []
  );
  const [enableViewportCulling, setEnableViewportCulling] = useState<boolean>(
    new URLSearchParams(search).get("enable_smart_rendering") === "true"
  );
  const [isIntegrationInCanaryTesting, setIsIntegrationInCanaryTesting] = useState<boolean>(false);

  const [blueprintReadme, setBlueprintReadme] = useState<string | null>(null);
  const [codeBlockBranch, setCodeBlockBranch] = useState<CodeBlockBranchValue>(
    CodeBlockBranch.MAIN
  );

  const blueprint = blueprintState as Blueprint | undefined;
  const blueprintName = blueprint?.name;

  useDocumentTitle(blueprint?.human_name ?? blueprint?.name, [
    blueprint?.name,
    blueprint?.human_name,
  ]);

  const applyDefaultSchedules = (blueprint: Blueprint, schedule: BlueprintTrigger) => {
    const shouldBeScheduled = BLUEPRINT_OPERATION_TYPES_WITH_SCHEDULES.includes(
      blueprint?.operation_type
    );
    const default_unit = BlueprintTriggerIntervalUnit.HOURS;
    const default_slow_value = shouldBeScheduled ? 6 : 0;
    const default_intermediate_value = shouldBeScheduled ? 3 : 0;
    const default_fast_value = shouldBeScheduled ? 1 : 0;

    function generateSchedule(
      speed: BlueprintTriggerSpeed,
      value: number
    ): BlueprintTriggerSchedule {
      return {
        speed: speed,
        schedule_interval_value: value,
        schedule_interval_unit: default_unit,
      };
    }

    return {
      slow_trigger_schedule:
        schedule.slow_trigger_schedule == null
          ? generateSchedule(BlueprintTriggerSpeed.BLUEPRINT_SPEED_SLOW, default_slow_value)
          : schedule.slow_trigger_schedule,

      intermediate_trigger_schedule:
        schedule.intermediate_trigger_schedule == null
          ? generateSchedule(
              BlueprintTriggerSpeed.BLUEPRINT_SPEED_INTERMEDIATE,
              default_intermediate_value
            )
          : schedule.intermediate_trigger_schedule,

      fast_trigger_schedule:
        schedule.fast_trigger_schedule == null
          ? generateSchedule(BlueprintTriggerSpeed.BLUEPRINT_SPEED_FAST, default_fast_value)
          : schedule.fast_trigger_schedule,
    };
  };
  useEffect(() => {
    if (blueprint) {
      setStepRelationMap(initializeStepRelationMap(blueprint));
    }
  }, [blueprintState]);

  useEffect(() => {
    if (!blueprint) return;
    const result = new Map<string, Set<string>>();
    const bpSteps = getAllStepsForBlueprint(blueprint);

    bpSteps.forEach((step) => {
      const staleKeys = getStaleParameterValueKeysForStep(blueprint, step);
      if (staleKeys.size > 0) {
        result.set(step.id, staleKeys);
      }
    });

    setFrontendStaleParameters(result);
  }, [
    JSON.stringify(blueprint?.steps),
    blueprint?.parameter_schema,
    blueprint?.global_var_json_schema,
  ]);

  const setBlueprint = useCallback(
    (newBlueprint: Blueprint | undefined) => {
      if (newBlueprint && !isEqual(blueprint, newBlueprint)) {
        blueprintActions.set(newBlueprint);
      }
    },
    [blueprint, blueprintActions]
  );

  // Update selected step if changed
  useEffect(() => {
    if (selectedStep?.id && blueprint) {
      const currentStep = getBlueprintStepForStepID(blueprint, selectedStep.id);
      if (currentStep) {
        setSelectedSteps(undefined);
        if (!isEqual(currentStep, selectedStep)) {
          setSelectedStep(currentStep);
        }
      }
    }
  }, [blueprint, blueprint?.steps, selectedStep]);

  const selectedStepContextValue = useMemo(() => {
    return selectedStep;
  }, [selectedStep]);

  useEffect(() => {
    setSelectedStep(blueprint?.steps[0]);
    // intentionally do not want to update when steps update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprint?.version?.id]);

  useEffect(() => {
    // Both blueprints are undefined
    if (!blueprint && !originalBlueprint) {
      setDoesBlueprintHaveUnsavedChanges(false);
      return;
    }
    // One blueprint is undefined and the other isn't
    if (!blueprint || !originalBlueprint) {
      setDoesBlueprintHaveUnsavedChanges(true);
      return;
    }

    // Now we know that both blueprints are defined, let's
    // check their contents.

    // First, do the cheap XOR checks for one empty one non-empty
    const newBlueprintSteps = blueprint?.steps;
    const originalBlueprintSteps = originalBlueprint?.steps;

    const blueprintParameterSchema = blueprint?.parameter_schema;
    const originalBlueprintParameterSchema = originalBlueprint?.parameter_schema;

    const blueprintQBXMLQueryRequestFormat = blueprint?.qbxml_query_request_format;
    const originalBlueprintQBXMLQueryRequestFormat = originalBlueprint?.qbxml_query_request_format;

    if (!newBlueprintSteps !== !originalBlueprintSteps) {
      // One set of steps exists and the other doesn't,
      // so skip string dump and set to true.
      setDoesBlueprintHaveUnsavedChanges(true);
      return;
    }
    if (!blueprintParameterSchema !== !originalBlueprintParameterSchema) {
      // One has a parameter schema and the other doesn't,
      // so skip string dump and set to true.
      setDoesBlueprintHaveUnsavedChanges(true);
      return;
    }

    if (blueprintQBXMLQueryRequestFormat !== originalBlueprintQBXMLQueryRequestFormat) {
      setDoesBlueprintHaveUnsavedChanges(true);
      return;
    }

    // Check for step changes
    if (JSON.stringify(blueprint.steps) !== JSON.stringify(originalBlueprint.steps)) {
      setDoesBlueprintHaveUnsavedChanges(true);
      return;
    }
    // Check for input parameter schema changes
    if (
      JSON.stringify(blueprintParameterSchema) !== JSON.stringify(originalBlueprintParameterSchema)
    ) {
      setDoesBlueprintHaveUnsavedChanges(true);
      return;
    }

    // All clear, no changes
    setDoesBlueprintHaveUnsavedChanges(false);
  }, [
    blueprint,
    JSON.stringify(blueprint?.steps),
    blueprint?.parameter_schema,
    blueprint?.qbxml_query_request_format,
    originalBlueprint,
  ]);

  // Tracks the version already requested, so a re-render mid-flight cannot refetch it.
  const requestedBlueprintVersionIDRef = useRef<string | undefined>();

  useEffect(() => {
    if (blueprint && blueprintTrigger) {
      setBlueprint(undefined);
    }

    if (
      blueprint?.version?.id !== blueprintVersionID &&
      requestedBlueprintVersionIDRef.current !== blueprintVersionID
    ) {
      requestedBlueprintVersionIDRef.current = blueprintVersionID;
      fetchBlueprintVersionForIntegration({
        integrationID,
        blueprintVersionID,
        onError: () => {
          // Clear the marker so the next render can retry.
          requestedBlueprintVersionIDRef.current = undefined;
          showErrorToast("Failed to fetch Blueprint version");
        },
        onSuccess: (response: BlueprintWithTrigger) => {
          const { schedule, ...blueprint } = response;
          setBlueprint(blueprint);
          setOriginalBlueprint(JSON.parse(JSON.stringify(blueprint)));
          setBlueprintTrigger(applyDefaultSchedules(blueprint, schedule));

          // Restore code_block_branch from step metadata if set (search nested paths too)
          const findCodeBlockBranch = (steps: any[]): string | undefined => {
            for (const step of steps) {
              if (
                step.template?.step_type === "CODE_BLOCK" &&
                step.template?.metadata?.code_block_branch
              ) {
                return step.template.metadata.code_block_branch;
              }
              if (step.paths && typeof step.paths === "object") {
                for (const pathSteps of Object.values(step.paths)) {
                  if (!Array.isArray(pathSteps)) continue;
                  const found = findCodeBlockBranch(pathSteps);
                  if (found) return found;
                }
              }
            }
            return undefined;
          };
          const restoredBranch = findCodeBlockBranch(blueprint.steps ?? []);
          if (restoredBranch) {
            setCodeBlockBranch(restoredBranch);
          }
        },
      });
    }
  }, [integrationID, blueprintVersionID, blueprint, blueprintTrigger, setBlueprint]);

  useEffect(() => {
    if (blueprint?.id) {
      fetchBlueprintVersions({
        blueprintID: blueprint?.id,
        onSuccess: (versions: BlueprintVersion[]) => setBlueprintVersions(versions),
      });
    }
  }, [blueprint]);

  useEffect(() => {
    fetchStepTemplates({
      integrationID,
      blueprintVersionID,
      onSuccess: (data) => {
        setStepTemplates(data);
      },
    });
    fetchGenericStepTemplates({
      integrationID,
      blueprintVersionID,
      onSuccess: (data) => {
        setGenericStepTemplates(data);
      },
    });
    fetchTemplateConfigs({
      integrationID,
      onSuccess: (data) => {
        setAvailableTemplateConfigs(data);
      },
    });
    fetchReportFilesByBlueprintVersionID({
      blueprintVersionID,
      onSuccess: (reportFiles: Array<ReportFile>) => setReportFiles(reportFiles),
    });
    fetchStaleParametersByBlueprintVersionID({
      blueprintVersionID,
      onSuccess: (response: BlueprintVersionStaleParametersInfo) => {
        if (Object.keys(response ?? {}).length > 0) {
          setStaleParameters(response);
        }
      },
      onError: () => {
        showErrorToast("Failed to fetch backend stale params");
      },
    });
    fetchCanaryTestInfo({
      integrationID,
      onSuccess: (response: CanaryTestInfo) => {
        setIsIntegrationInCanaryTesting(!!response?.is_integration_in_canary_testing);
      },
      onError: () => {
        showErrorToast("Failed to fetch canary testing info");
      },
    });
  }, [integrationID, blueprintVersionID]);

  useEffect(() => {
    setBlueprintRunnerExecutionResponse(null);
  }, [blueprint]);

  // Track previous branch for detecting changes
  const prevCodeBlockBranchRef = useRef(codeBlockBranch);

  // Auto-replace code block templates when branch changes
  useEffect(() => {
    // Skip on initial render
    if (prevCodeBlockBranchRef.current === codeBlockBranch) {
      return;
    }
    prevCodeBlockBranchRef.current = codeBlockBranch;

    if (!blueprint?.steps) {
      return;
    }

    // Find all code block steps
    const codeBlockSteps = blueprint.steps.filter(
      (step) => step.template?.step_type === BlueprintStepType.CodeBlock
    );

    if (codeBlockSteps.length === 0) {
      return;
    }

    // Fetch all templates in parallel, then update blueprint once
    const templatePromises = codeBlockSteps.map((step) => {
      const codeBlockName = step.template?.metadata?.code_block;
      const codeBlockVersion = step.template?.metadata?.version;

      if (!codeBlockName || !codeBlockVersion) {
        return Promise.resolve(null);
      }

      return new Promise<{ step: BlueprintStep; template: BlueprintStepTemplate } | null>(
        (resolve) => {
          fetchCodeBlockTemplate({
            blockName: codeBlockName,
            versionName: codeBlockVersion,
            branch: codeBlockBranch,
            onSuccess: (template) => {
              resolve({ step, template: { ...template, id: step.template.id } });
            },
            onError: () => {
              showErrorToast(
                `Failed to fetch template for ${codeBlockName} version ${codeBlockVersion} from ${codeBlockBranch} branch`
              );
              resolve(null);
            },
          });
        }
      );
    });

    Promise.all(templatePromises).then((results) => {
      let updatedBlueprint = blueprint;
      results.forEach((result) => {
        if (result) {
          const { step, template } = result;
          const currentStep = updatedBlueprint.steps.find((s) => s.id === step.id);
          if (currentStep) {
            const { blueprint: newBlueprint } = updateBlueprintForNewStepTemplate(
              updatedBlueprint,
              currentStep,
              template
            );
            updatedBlueprint = newBlueprint;
          }
        }
      });
      setBlueprint(updatedBlueprint);
    });
  }, [codeBlockBranch]);

  function addStep<T extends BlueprintStepType>(
    stepTemplate: BlueprintStepTemplateBase<T, any>,
    newStepRelation: AddStepRelation,
    relatedStepID: string,
    newStepTypePathKey?: string
  ) {
    if (blueprint) {
      const response = addNewStepFromTemplate<T>(
        blueprint,
        stepTemplate,
        relatedStepID,
        newStepRelation,
        newStepTypePathKey
      );
      setBlueprint(JSON.parse(JSON.stringify(response.blueprint)));
      setSelectedStep(response.step);
    }
  }

  function addGenericStep(
    stepTemplate: BlueprintGenericStepTemplate,
    newStepRelation: AddStepRelation,
    relatedStepID: string,
    newStepTypePathKey?: string
  ) {
    if (blueprint) {
      const response = addNewGenericStepFromTemplate(
        blueprint,
        stepTemplate,
        relatedStepID,
        newStepRelation,
        newStepTypePathKey
      );
      setBlueprint(JSON.parse(JSON.stringify(response.blueprint)));
      setSelectedStep(response.step);
    }
  }

  function addCopiedStep(
    step: BlueprintStep,
    newStepRelation: AddStepRelation,
    relatedStepID: string,
    newStepTypePathKey?: string
  ) {
    if (blueprint) {
      const response = addNewCopiedStep(
        blueprint,
        step,
        relatedStepID,
        newStepRelation,
        newStepTypePathKey
      );

      setBlueprint(response.blueprint);
      setSelectedStep(response.step);
      onSetCopiedStep(response.step);
    }
  }

  function onSetCopiedStep(step: BlueprintStep | undefined) {
    if (step) {
      setCopiedStep(step);
      setCopiedSteps(undefined);
    }
  }
  function onSetCopiedSteps(steps: BlueprintStepOrGhostStepOrTriggerOrScraper[] | undefined) {
    if (steps && steps.length > 0) {
      setCopiedStep(undefined);
      setCopiedSteps(steps);
    }
  }

  async function addCopiedStepsFromClipboard(
    newStepRelation: AddStepRelation,
    relatedStepID: string,
    newStepTypePathKey?: string
  ) {
    const text = await navigator.clipboard.readText();
    try {
      const parsedText = JSON.parse(text);
      if (Array.isArray(parsedText)) {
        addCopiedSteps(
          parsedText as BlueprintStep[],
          newStepRelation,
          relatedStepID,
          newStepTypePathKey
        );
      } else {
        addCopiedStep(parsedText, newStepRelation, relatedStepID, newStepTypePathKey);
      }
    } catch (err) {
      showErrorToast(`Failed to load step(s) from clipboard: ${err}`);
    }
  }

  function addCopiedSteps(
    steps: BlueprintStep[],
    newStepRelation: AddStepRelation,
    relatedStepID: string,
    newStepTypePathKey?: string
  ) {
    if (blueprint && steps.length > 0) {
      let response = null;
      for (const [index, step] of steps.entries()) {
        if (index === 0) {
          response = addNewCopiedStep(
            blueprint,
            step,
            relatedStepID,
            newStepRelation,
            newStepTypePathKey
          );
        } else if (response) {
          response = addNewCopiedStep(
            response.blueprint,
            step,
            response.step.id,
            AddStepRelation.SIBLING_AFTER
          );
        }
      }
      if (response) {
        setBlueprint(response.blueprint);
        setSelectedStep(response.step);
      }
    }
  }

  function onDeleteStep(stepID: string) {
    if (blueprint != null) {
      setBlueprint(deleteStep(blueprint, stepID));
      setSelectedStep(blueprint.steps.length > 0 ? blueprint.steps[0] : undefined);
    }
  }

  function onDeleteSteps(steps: Array<BlueprintStep>) {
    if (blueprint != null) {
      setBlueprint(deleteSteps(blueprint, steps));
      setSelectedStep(blueprint.steps.length > 0 ? blueprint.steps[0] : undefined);
    }
  }

  function onDeleteIfElseStepAndMoveChildrenUp(step: BlueprintIfElseStep) {
    if (blueprint != null) {
      setBlueprint(deleteIfElseStepAndMoveChildrenUp(blueprint, step));
      setSelectedStep(blueprint.steps.length > 0 ? blueprint.steps[0] : undefined);
    }
  }

  function onCollapseSubsteps(stepID: string) {
    if (blueprint != null) {
      setBlueprint(collapseSubsteps(blueprint, stepID));
    }
  }

  const updateStepParameterValues = (
    step: BlueprintStep,
    parameterValues: { [key: string]: BlueprintParameterValue | null }
  ) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateBlueprintForNewParameterValues(
        blueprint,
        step,
        parameterValues
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const updateStepParameterValue = (step: BlueprintStep, valueKey: string, value: any) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateBlueprintForNewParameterValue(
        blueprint,
        step,
        valueKey,
        value
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  /*
   * When swapping in a new step template, only carry forward parameter_values entries that:
   *   1. Existed on the previous step, and
   *   2. are still declared in the new template’s parameter_schema.properties or top level keys on parameter_schema
   *
   * This guards against “phantom” keys—stale parameter_values left over from a prior template
   * that no longer apply.
   */

  const replaceStepTemplate = (
    step: BlueprintStep,
    newTemplate: BlueprintStepTemplate | BlueprintGenericStepTemplate
  ) => {
    if (!blueprint) return;

    // 1. swap in the new template
    const { blueprint: tempBlueprint, step: tempStep } = updateBlueprintForNewStepTemplate(
      blueprint,
      step,
      newTemplate
    );

    // 2. drop any parameter_values keys that aren't declared on the new template's schema
    const filteredParameterValues = filterParameterValuesForNewTemplate(
      tempStep.parameter_values,
      newTemplate
    ) as typeof tempStep.parameter_values;

    // 3. replace the updated step wherever it actually lives in the blueprint -- steps can be
    // nested inside another step's `paths` (loops, if/else branches, etc.), so this must use the
    // same path-aware lookup updateBlueprintForNewStepTemplate used above, not a flat top-level
    // `blueprint.steps` scan (which silently no-ops for nested steps).
    const finalStep = { ...tempStep, parameter_values: filteredParameterValues };
    const { blueprint: finalBlueprint } = updateStep(tempBlueprint, finalStep);

    setBlueprint(finalBlueprint);
    setSelectedStep(finalStep);
  };

  const updateSwitchStepOption = (
    switchStep: BlueprintSwitchStep,
    optionKey: string,
    modificationType: ModifySwitchOptionType
  ) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = modifySwitchOption(
        blueprint,
        switchStep,
        optionKey,
        modificationType
      );
      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const renameStep = (stepID: string, newName: string, onError?: () => void) => {
    if (blueprint != null) {
      const updatedBlueprintAndStep: {
        blueprint: Blueprint;
        step: BlueprintStep;
      } | null = renameBlueprintStep(blueprint, stepID, newName);

      if (updatedBlueprintAndStep) {
        setBlueprint(updatedBlueprintAndStep.blueprint);
        setSelectedStep(updatedBlueprintAndStep.step);
      } else if (onError && stepID !== newName) {
        showErrorToast("Error saving step! Step name already exists.");
        onError();
      }
    }
  };

  const setRunStepConcurrently = (stepId: string, runConcurrently: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateBlueprintStepConcurrency(
        blueprint,
        stepId,
        runConcurrently
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setStepUsesPaginationTimestamp = (stepId: string, usePaginationTimestamp: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateUsePaginationTimestamp(
        blueprint,
        stepId,
        usePaginationTimestamp
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setStepIgnoreAuthConfig = (stepId: string, ignoreAuthConfig: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateIgnoreAuthConfig(
        blueprint,
        stepId,
        ignoreAuthConfig
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setStepAllowIncompatibleTypes = (stepId: string, allowIncompatibleTypes: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateAllowIncompatibleTypes(
        blueprint,
        stepId,
        allowIncompatibleTypes
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setStepNeedsRemoteData = (stepId: string, needsRemoteData: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateNeedsRemoteData(
        blueprint,
        stepId,
        needsRemoteData
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setStepFreeFileAfterUpload = (stepId: string, freeFileAfterUpload: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateFreeFileAfterUpload(
        blueprint,
        stepId,
        freeFileAfterUpload
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setStepClosesPaginationSequence = (stepId: string, closesPaginationSequence: boolean) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateClosesPaginationSequence(
        blueprint,
        stepId,
        closesPaginationSequence
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const updateStepMockResponseBody = (step: BlueprintStep, key: string, value: string) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint, step: newStep } = updateBlueprintForNewMockResponseBody(
        blueprint,
        step,
        key,
        value
      );

      setBlueprint(newBlueprint);
      setSelectedStep(newStep);
    }
  };

  const setBlueprintParameterSchema = (parameterSchema: JSONObjectSchema) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint } = setParameterSchemaForBlueprint(
        blueprint,
        parameterSchema
      );

      setBlueprint(newBlueprint);
    }
  };

  const setBlueprintQBXMLQueryRequestFormat = (qbXMLRequestBodyFormat: string) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint } = setQBXMLQueryRequestFormatForBlueprint(
        blueprint,
        qbXMLRequestBodyFormat
      );

      setBlueprint(newBlueprint);
    }
  };

  const setBlueprintReturnSchema = (returnSchema: JSONObjectSchema) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint } = setReturnSchemaForBlueprint(blueprint, returnSchema);

      setBlueprint(newBlueprint);
    }
  };

  const setBlueprintHumanName = (human_name: string) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint } = setHumanNameForBlueprint(blueprint, human_name);

      setBlueprint(newBlueprint);
    }
  };

  const setBlueprintCategory = (category: string) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint } = setCategoryForBlueprint(blueprint, category);

      setBlueprint(newBlueprint);
    }
  };

  const setBlueprintStepNote = (stepID: string, text: string) => {
    if (blueprint != null) {
      const { blueprint: newBlueprint } = setStepNoteForBlueprint(blueprint, stepID, text);

      setBlueprint(newBlueprint);
    }
  };

  /**
   * Function to compute traced steps
   * Called when "Trace" is clicked for a step's parameter
   */
  const computeTracedStepIDs = (newStepParameterToTrace: StepParameterTracingTarget) => {
    setTracedStepIDs(
      computeTracedStepIDsForBlueprint(blueprint?.steps ?? [], newStepParameterToTrace)
    );
  };

  const keyMaps = {
    UNDO: "command+z",
    REDO: ["command+y", "command+shift+z"],
    SAVE: "command+s",
    COPY: "command+c",
    PASTE: "command+v",
    PASTE_CLIPBOARD: "command+shift+v",
    CUT: "command+x",
    UP: "up",
    DOWN: "down",
    LEFT: "left",
    RIGHT: "right",
    SHIFT_UP: "shift+up",
    SHIFT_DOWN: "shift+down",
  };

  return (
    <>
      <Route exact path={BLUEPRINT_EDITOR_PATH}>
        <Redirect to={match.url + "/main"} />
      </Route>
      {blueprint && originalBlueprint && blueprintName ? (
        <BlueprintContextProvider
          addStep={addStep}
          addGenericStep={addGenericStep}
          addCopiedStep={addCopiedStep}
          addCopiedSteps={addCopiedSteps}
          addCopiedStepsFromClipboard={addCopiedStepsFromClipboard}
          blueprint={blueprint}
          blueprintVersions={blueprintVersions}
          blueprintTrigger={blueprintTrigger}
          setBlueprintTrigger={setBlueprintTrigger}
          deleteStep={onDeleteStep}
          deleteSteps={onDeleteSteps}
          deleteIfElseStepAndMoveChildrenUp={onDeleteIfElseStepAndMoveChildrenUp}
          collapseSubsteps={onCollapseSubsteps}
          setBlueprint={setBlueprint}
          setOriginalBlueprint={setOriginalBlueprint}
          setSelectedStep={setSelectedStep}
          blueprintRunnerExecutionResponse={blueprintRunnerExecutionResponse}
          setBlueprintRunnerExecutionResponse={setBlueprintRunnerExecutionResponse}
          updateStepParameterValues={updateStepParameterValues}
          updateStepParameterValue={updateStepParameterValue}
          replaceStepTemplate={replaceStepTemplate}
          selectedStep={selectedStepContextValue}
          stepTemplates={stepTemplates}
          genericStepTemplates={genericStepTemplates}
          updateSwitchStepOption={updateSwitchStepOption}
          copiedStep={copiedStep}
          setCopiedStep={onSetCopiedStep}
          renameStep={renameStep}
          doesBlueprintHaveUnsavedChanges={doesBlueprintHaveUnsavedChanges}
          updateStepMockResponseBody={updateStepMockResponseBody}
          undoActions={blueprintActions}
          setBlueprintParameterSchema={setBlueprintParameterSchema}
          setReportFiles={setReportFiles}
          reportFiles={reportFiles}
          backendStaleParameters={staleParameters}
          setBackendStaleParameters={setStaleParameters}
          frontendStaleParameters={frontendStaleParameters}
          setFrontendStaleParameters={setFrontendStaleParameters}
          setBlueprintReturnSchema={setBlueprintReturnSchema}
          setRunStepConcurrently={setRunStepConcurrently}
          setStepFreeFileAfterUpload={setStepFreeFileAfterUpload}
          setStepUsesPaginationTimestamp={setStepUsesPaginationTimestamp}
          setStepIgnoreAuthConfig={setStepIgnoreAuthConfig}
          setStepAllowIncompatibleTypes={setStepAllowIncompatibleTypes}
          setStepNeedsRemoteData={setStepNeedsRemoteData}
          setStepClosesPaginationSequence={setStepClosesPaginationSequence}
          setBlueprintParameterSchemaValue={blueprintActions.setBlueprintParameterSchemaValue}
          setBlueprintReturnSchemaValue={blueprintActions.setBlueprintReturnSchemaValue}
          setRequiredFieldsForObject={blueprintActions.setRequiredFieldsForObject}
          setBlueprintHumanName={setBlueprintHumanName}
          setBlueprintCategory={setBlueprintCategory}
          setBlueprintStepNote={setBlueprintStepNote}
          availableTemplateConfigs={availableTemplateConfigs}
          setAvailableTemplateConfigs={setAvailableTemplateConfigs}
          stepRelationMap={stepRelationMap}
          setStepRelationMap={setStepRelationMap}
          selectedSteps={selectedSteps}
          setSelectedSteps={setSelectedSteps}
          copiedSteps={copiedSteps}
          setCopiedSteps={onSetCopiedSteps}
          selectedStepLog={selectedStepLog}
          setSelectedStepLog={setSelectedStepLog}
          stepLoggingView={stepLoggingView}
          setStepLoggingView={setStepLoggingView}
          setBlueprintQBXMLQueryRequestFormat={setBlueprintQBXMLQueryRequestFormat}
          isTracing={isTracing}
          setIsTracing={setIsTracing}
          stepParameterToTrace={stepParameterToTrace}
          setStepParameterToTrace={setStepParameterToTrace}
          tracedStepIDs={tracedStepIDs}
          setTracedStepIDs={setTracedStepIDs}
          computeTracedStepIDs={computeTracedStepIDs}
          enableViewportCulling={enableViewportCulling}
          setEnableViewportCulling={setEnableViewportCulling}
          isIntegrationInCanaryTesting={isIntegrationInCanaryTesting}
          blueprintReadme={blueprintReadme}
          setBlueprintReadme={setBlueprintReadme}
          codeBlockBranch={codeBlockBranch}
          setCodeBlockBranch={setCodeBlockBranch}
          scrollToStep={scrollToStep}
          registerScrollToStep={registerScrollToStep}
        >
          <GlobalHotKeys keyMap={keyMaps}>
            <EditorLeavingGuard
              currentLocation={getBlueprintEditorPath(integrationID, blueprintVersionID)}
              hasUnsavedChanges={doesBlueprintHaveUnsavedChanges}
            >
              <BlueprintEditorView
                integrationID={integrationID}
                match={match}
                parameterSchema={blueprint.parameter_schema}
                returnSchema={blueprint.return_schema}
                operationType={blueprint.operation_type}
                hasUnsavedChanges={doesBlueprintHaveUnsavedChanges}
              />
            </EditorLeavingGuard>
          </GlobalHotKeys>
        </BlueprintContextProvider>
      ) : (
        <EmptyStateWrapper isSpinner />
      )}
    </>
  );
}

export default BlueprintEditor;
